import { useEffect, useMemo, useState } from "react";
import { kaylasApi } from "./kaylasApi";
import { KaylasDrawer, PhotoField, FormSection } from "./KaylasDrawer";
import { Plus, Search, Edit3, Trash2, Loader2, Heart, ExternalLink, Check } from "lucide-react";
import { toast } from "sonner";

const KINDS = ["dahlia", "seed", "flower", "project"];
const PRIORITIES = ["High", "Medium", "Low"];
const empty = {
  kind: "dahlia", name: "", supplier: "", estimated_cost: 0,
  priority: "Medium", notes: "", purchase_link: "", photo: null, purchased: false,
};

export default function KaylasWishlist() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [drawer, setDrawer] = useState(null);

  const load = async () => { setLoading(true); try { setItems(await kaylasApi.listWishlist()); } finally { setLoading(false); } };
  useEffect(() => {
    let off = false;
    (async () => {
      try { const r = await kaylasApi.listWishlist(); if (!off) setItems(r); }
      finally { if (!off) setLoading(false); }
    })();
    return () => { off = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items
      .filter((i) => (filter === "all" ? true : filter === "active" ? !i.purchased : filter === "got" ? i.purchased : i.kind === filter))
      .filter((i) => (q ? [i.name, i.supplier, i.notes].some((s) => (s || "").toLowerCase().includes(q)) : true));
  }, [items, search, filter]);

  const onDelete = async (id) => {
    if (!window.confirm("Remove from wishlist?")) return;
    await kaylasApi.deleteWish(id);
    toast.success("Removed");
    await load();
  };

  const togglePurchased = async (i) => {
    await kaylasApi.updateWish(i.id, { ...i, purchased: !i.purchased });
    toast.success(i.purchased ? "Back on wishlist" : "Ticked off — well done!");
    await load();
  };

  return (
    <div className="space-y-6" data-testid="mums-wishlist-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="mum-chip"><Heart size={12} /> Wishlist</div>
          <h1 className="font-display text-4xl mt-2">Garden Wishlist</h1>
          <p className="font-display italic text-[var(--mum-cobalt)] text-sm mt-1">All the dahlias, seeds and projects you&apos;re dreaming of.</p>
        </div>
        <button className="mum-btn" onClick={() => setDrawer("new")} data-testid="mums-wish-add"><Plus size={15} /> Add wish</button>
      </header>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--mum-cobalt)]/60" />
          <input className="mum-field pl-9" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="mums-wish-search" />
        </div>
        <select className="mum-field sm:w-56" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="mums-wish-filter">
          <option value="all">All wishes</option>
          <option value="active">Still wanted</option>
          <option value="got">Already got</option>
          <option disabled>──</option>
          {KINDS.map((k) => <option key={k} value={k}>{k.charAt(0).toUpperCase() + k.slice(1)}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 size={24} className="animate-spin mx-auto text-[var(--mum-cobalt)]" /></div>
      ) : filtered.length === 0 ? (
        <div className="kaylas-card-quiet p-10 text-center">
          <Heart size={36} className="text-[var(--mum-cobalt)]/60 mx-auto" />
          <p className="font-display italic text-[var(--mum-cobalt)] text-lg mt-3">Nothing wished for yet.</p>
          <button className="mum-btn mt-5" onClick={() => setDrawer("new")}><Plus size={15} /> Add first wish</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="mums-wish-grid">
          {filtered.map((i) => (
            <article key={i.id} className={`kaylas-card overflow-hidden flex flex-col ${i.purchased ? "opacity-70" : ""}`} data-testid={`wish-${i.id}`}>
              {i.photo && <div className="aspect-[4/3]"><img src={i.photo} alt={i.name} className="w-full h-full object-cover" loading="lazy" /></div>}
              <div className="p-4 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display text-xl text-[var(--mum-sapphire)]">{i.name}</h3>
                  <span className={`text-[0.65rem] uppercase tracking-wider px-2 py-0.5 rounded-full ${i.priority === "High" ? "bg-[var(--mum-cobalt)] text-[var(--mum-ivory)]" : i.priority === "Medium" ? "bg-[var(--mum-cornflower)]/30 text-[var(--mum-sapphire)]" : "bg-[var(--mum-sky)] text-[var(--mum-sapphire)]"}`}>{i.priority}</span>
                </div>
                <p className="text-xs text-[var(--mum-cobalt)]/75 mt-1">
                  {i.kind} {i.supplier ? `· ${i.supplier}` : ""} {i.estimated_cost ? `· $${i.estimated_cost}` : ""}
                </p>
                {i.notes && <p className="text-sm mt-2 line-clamp-3">{i.notes}</p>}
                {i.purchase_link && <a href={i.purchase_link} target="_blank" rel="noopener noreferrer" className="text-xs underline mt-1 inline-flex items-center gap-1 text-[var(--mum-cobalt)]"><ExternalLink size={10} /> Link</a>}
                <div className="flex gap-2 mt-auto pt-3">
                  <button className="mum-btn-ghost flex-1" onClick={() => togglePurchased(i)} data-testid={`wish-toggle-${i.id}`}><Check size={13} /> {i.purchased ? "Re-add" : "Got it"}</button>
                  <button className="mum-btn-ghost" onClick={() => setDrawer({ edit: i })} data-testid={`wish-edit-${i.id}`}><Edit3 size={13} /></button>
                  <button className="mum-btn-ghost" onClick={() => onDelete(i.id)} data-testid={`wish-delete-${i.id}`}><Trash2 size={13} /></button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <KaylasDrawer open={drawer !== null} onClose={() => setDrawer(null)} title={drawer?.edit ? "Edit wish" : "Add wish"} testid="mums-wish-drawer">
        <WishForm initial={drawer?.edit || empty} onClose={() => setDrawer(null)} onSaved={async () => { setDrawer(null); await load(); }} />
      </KaylasDrawer>
    </div>
  );
}

function WishForm({ initial, onClose, onSaved }) {
  const editing = !!initial.id;
  const [form, setForm] = useState({ ...empty, ...initial });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const payload = { ...form, estimated_cost: Number(form.estimated_cost) || 0 };
      if (editing) await kaylasApi.updateWish(initial.id, payload);
      else await kaylasApi.createWish(payload);
      toast.success(editing ? "Wish updated" : "Wish added");
      onSaved();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Couldn't save");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-5">
      <FormSection title="The Wish">
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">What you want *</span>
          <input className="mum-field mt-1.5" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="wish-name" />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Kind</span>
            <select className="mum-field mt-1.5" value={form.kind} onChange={(e) => set("kind", e.target.value)}>
              {KINDS.map((k) => <option key={k}>{k}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Priority</span>
            <select className="mum-field mt-1.5" value={form.priority} onChange={(e) => set("priority", e.target.value)}>
              {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
            </select>
          </label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Supplier</span>
            <input className="mum-field mt-1.5" value={form.supplier} onChange={(e) => set("supplier", e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Estimated cost ($)</span>
            <input type="number" min="0" step="0.5" className="mum-field mt-1.5" value={form.estimated_cost} onChange={(e) => set("estimated_cost", e.target.value)} />
          </label>
        </div>
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Purchase link</span>
          <input className="mum-field mt-1.5" type="url" value={form.purchase_link || ""} onChange={(e) => set("purchase_link", e.target.value)} placeholder="https://…" />
        </label>
        <PhotoField label="Inspiration photo" value={form.photo} onChange={(v) => set("photo", v)} testid="wish-photo" />
        <textarea className="mum-field" rows={3} placeholder="Notes" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} />
      </FormSection>
      <div className="flex gap-2 sticky bottom-0 bg-[var(--mum-paper)] pt-3 -mx-5 px-5 pb-1 border-t border-[var(--mum-sky)]">
        <button type="button" className="mum-btn-ghost flex-1" onClick={onClose}>Cancel</button>
        <button type="submit" className="mum-btn flex-1" disabled={busy}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          {editing ? "Save" : "Add wish"}
        </button>
      </div>
    </form>
  );
}
